import { Suspense } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router';

import { RequireAuth } from '@/auth/RequireAuth';
import { LoginPage } from '@/features/auth/LoginPage';
import { NotPermittedPage } from '@/features/auth/NotPermittedPage';
import { ModulePlaceholderPage } from '@/features/modules/ModulePlaceholderPage';
import { PublicInvoicePage } from '@/modules/billing/routes';
import { PublicLeadPage, PublicProposalPage } from '@/modules/core/routes';
import { FieldRoutes } from '@/modules/field/routes';
import { useModules } from '@/modules/ModuleContext';
import { CHILD_ROUTES, IMPLEMENTED, pageKeyOf } from '@/modules/routes';
import { AppShell } from '@/shell/AppShell';
import { VIEWS, type NavView } from '@/shell/navigation';
import { HomeRoute } from './HomeRoute';
import { movedTo } from './movedAddresses';
import { RouteLoadBoundary } from './RouteLoadBoundary';

/**
 * Wraps every signed-in screen in the shell.
 *
 * An address that used to hold a configuration screen is sent to its place in Settings before
 * anything is drawn, so a bookmark or an old link never shows the screen it used to.
 */
function ShellLayout(): React.JSX.Element {
  const { pathname, search } = useLocation();
  const moved = movedTo(pathname, search);
  if (moved !== null) {
    return <Navigate to={moved} replace />;
  }
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

function ModuleUnavailable(): React.JSX.Element {
  return (
    <main>
      <h1>Module unavailable</h1>
      <p>This module is not included in this release.</p>
    </main>
  );
}

/** A view's screen and the routes beneath it, behind the capability the view asks for. */
function viewRoutes(view: NavView): React.JSX.Element | null {
  const key = pageKeyOf(view.id);
  if (key === undefined) {
    return null;
  }
  const Page = IMPLEMENTED[key] ?? ModulePlaceholderPage;
  const [path] = view.paths;

  return (
    <Route
      key={view.id}
      element={
        view.permission === undefined ? <Outlet /> : <RequireAuth permission={view.permission} />
      }
    >
      <Route path={path} element={<Page />} />
      {(CHILD_ROUTES[key] ?? []).map((child) => {
        const ChildPage = child.element;
        const route = (
          <Route key={child.path} path={`${path}/${child.path}`} element={<ChildPage />} />
        );
        return child.permission === undefined ? (
          route
        ) : (
          <Route key={child.path} element={<RequireAuth permission={child.permission} />}>
            {route}
          </Route>
        );
      })}
    </Route>
  );
}

/**
 * The route tree.
 *
 * Guarded branches are nested inside {@link RequireAuth} rather than each page
 * checking for itself. A page that guards itself is a page someone can forget
 * to guard; a branch that is guarded once covers every route added beneath it,
 * including the ones later stages add.
 *
 * The module routes are generated from the navigation registry, so a view
 * cannot appear in the shell without a route, or exist as a route nobody can
 * find. A view whose module is not in this release resolves to "Module
 * unavailable" under its own paths rather than to a page that fails.
 *
 * @returns the routes element
 */
export function AppRoutes(): React.JSX.Element {
  const enabled = useModules();
  // Today is the home route, and field mode gets its own chrome on purpose: the generator wraps
  // every route in the workspace shell, and a handset gets a different frame. The registry entry
  // still earns its place: it is what attaches JOB_FIELD_EXECUTE to the path.
  const routed = VIEWS.filter(
    (view) => view.id !== 'today' && view.id !== 'field' && enabled(view.module),
  );
  const unavailable = VIEWS.filter((view) => !enabled(view.module));

  return (
    <RouteLoadBoundary>
      <Suspense
        fallback={
          <main>
            <h1>Loading page</h1>
            <p role="status">Loading page...</p>
          </main>
        }
      >
        <Routes>
          <Route path="/public/intake/:slug" element={<PublicLeadPage />} />
          <Route path="/public/proposal/:slug/:token" element={<PublicProposalPage />} />
          {enabled('BILLING') && (
            <Route path="/public/invoice/:slug/:token" element={<PublicInvoicePage />} />
          )}
          <Route path="/sign-in" element={<LoginPage />} />
          {!enabled('BILLING') && (
            <Route
              path="/public/invoice/*"
              element={
                <main>
                  <h1>Module unavailable</h1>
                </main>
              }
            />
          )}

          <Route element={<RequireAuth />}>
            {enabled('FIELD') && (
              <Route element={<RequireAuth permission="JOB_FIELD_EXECUTE" />}>
                <Route path="/field/*" element={<FieldRoutes />} />
              </Route>
            )}

            <Route element={<ShellLayout />}>
              <Route path="/" element={<HomeRoute />} />
              <Route path="/not-permitted" element={<NotPermittedPage />} />
              {unavailable.flatMap((view) =>
                view.paths.map((path) => (
                  <Route
                    key={`${view.id}:${path}`}
                    path={`${path}/*`}
                    element={<ModuleUnavailable />}
                  />
                )),
              )}

              {routed.map(viewRoutes)}

              {/* Unknown paths are treated as protected, so a mistyped URL cannot be
              used to find out which routes exist without signing in first. */}
              <Route path="*" element={<HomeRoute />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </RouteLoadBoundary>
  );
}
